import { throttle } from 'lodash'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { animated, useTransition } from 'react-spring'
import styled, { css } from 'styled-components'

import BackToTopSvg from '../../public/static/images/back-to-top.svg'
import { getColor, safeArea } from '../../utils/cssUtils'

import mq from '../../utils/mediaQuery'
import ScrollTo from '../ScrollTo'
import SvgIcon from '../SvgIcon'

const BackToTopBtn = styled(animated.button)`
  width: 48px;
  height: 48px;
  border: none;
  border-radius: 50%;
  background-color: ${getColor('lightYellow')};
  display: flex;
  justify-content: center;
  align-items: center;
  position: fixed;
  right: 24px;
  bottom: 24px;
  box-shadow: 0 2px 10px 0 rgba(0, 0, 0, 0.2);
  outline: none;
  cursor: pointer;
  ${safeArea(
    inset => css`
      bottom: calc(${inset} + 24px);
    `
  )}
  ${mq.mobile} {
    right: 12px;
    bottom: 68px;
    ${safeArea(
      inset => css`
        bottom: calc(${inset} + 68px);
      `
    )}
  }
`

const BackToTop = ({ backTo = -1 }) => {
  const container = useRef()

  const [shouldScroll, setShouldScroll] = useState(false)

  const [showBackToTop, setShowBackToTop] = useState(false)

  const getScrollTop = useCallback(
    () => document.body.scrollTop || document.documentElement.scrollTop,
    []
  )

  const handleScrollToTop = e => {
    setShouldScroll(true)
  }

  const handleOnStop = () => {
    setShouldScroll(false)
  }

  const handleScroll = useCallback(
    throttle(e => {
      const scrollTop = getScrollTop()
      setShowBackToTop(scrollTop > window.innerHeight)
    }, 200),
    []
  )

  useEffect(() => {
    container.current = document.getElementById('back-to-top-root')
    window.addEventListener('scroll', handleScroll)
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  const transition = useTransition(showBackToTop, {
    from: { opacity: 0, transform: 'scale(0)' },
    enter: { opacity: 1, transform: 'scale(1)' },
    leave: { opacity: 0, transform: 'scale(0)' },
    expires: 0
  })

  return (
    <>
      {container.current
        ? createPortal(
            transition(
              (styles, show) =>
                show && (
                  <BackToTopBtn
                    style={styles}
                    onClick={handleScrollToTop}
                    data-test-id='back-to-top'
                  >
                    <SvgIcon svg={<BackToTopSvg />} />
                  </BackToTopBtn>
                )
            ),
            container.current
          )
        : null}
      <ScrollTo
        shouldScroll={shouldScroll}
        onStop={handleOnStop}
        scrollTo={backTo}
      />
    </>
  )
}

export default BackToTop
